import Button from '@components/Button';
import Checkbox from '@components/Checkbox';
import Column from '@components/Column';
import Divider from '@components/Divider';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import useModal from '@hooks/ui/useModal';
import { DiscountCouponRow } from '@models/discount';
import { OrderLine } from '@models/order';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import useCouponControl from '@store/order/hooks/useCouponControl';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { Fragment, useContext, useMemo } from 'react';

import styles from './styles.module.scss';

interface Props {
    coupon: DiscountCouponRow;
}

function DiscountCoupon({ coupon }:Props) {
  const { discounts, orderLines } = useCalculatedRequest();
  const { toggleDiscountCoupon, clearDiscountCoupon } = useCouponControl();
  const {
    id, expiresAt,
    discountMethod,
    discountTarget,
    discountValue,
    maxDiscountAmount,
    minOrderAmount, applicableItemIds, title,
  } = coupon;
  const isChecked = discounts
    ?.map((d) => d.couponId).includes(coupon.id);
  const otherChecked = (
    discounts
      ?.filter((discount) => !!discount.couponId)?.length || 0
  ) > 0 && !isChecked;
  const { openModal, closeModal } = useModal();
  // 적용 가능한 상품
  const actualApplicableItemIds = useMemo(() => {
    const selectedItemIds = orderLines.map((item) => item.shopItemId);
    if (applicableItemIds && applicableItemIds.length !== 0) {
      return selectedItemIds.filter((itemId) => applicableItemIds?.includes(itemId));
    }
    return selectedItemIds;
  }, [applicableItemIds, orderLines]);

  const handleSelectCoupon = () => {
    if (discountTarget === 'ITEM') {
      //  여러 상품에 적용 가능한 경우
      if (actualApplicableItemIds.length > 1) {
        selectItem();
        return;
      }
      const itemId = actualApplicableItemIds[0];
      const orderLine = orderLines
        .find((it) => it.shopItemId === itemId);
      toggleDiscountCoupon(coupon, isSelected(orderLine!), orderLine!);
    } else {
      toggleDiscountCoupon(coupon, isChecked || false);
    }
  };

  const isSelected = (orderLine: OrderLine) => {
    const { shopItemId, defaultOptions, personalOptions } = orderLine;
    const optionIds = [...defaultOptions, ...personalOptions]
      .map((it) => it.options)
      .flat()
      .map((it) => it.shopOptionId);
    return !!discounts?.find(
      (d) => d.couponId === coupon.id && d.itemId === shopItemId
          && isEqual(d.optionIds, optionIds),
    );
  };

  const handleSelectItem = (orderLine: OrderLine) => {
    closeModal();
    toggleDiscountCoupon(coupon, isSelected(orderLine), orderLine);
  };

  const selectItem = () => {
    const actualApplicableItems = orderLines
      .filter((line) => actualApplicableItemIds.includes(line.shopItemId)
          && line.couponUsable);
    openModal(<Column fullWidth spacing={20}
                      align='center' >
          <Text align='center' color={colors.gray6}>
              할인을 적용할 상품을 선택해주세요.
          </Text>
        <Column fullWidth spacing={20}>
            {actualApplicableItems.map((item, index) => {
              const { shopItemId, personalOptions, defaultOptions } = item;
              const options = [...personalOptions, ...defaultOptions]
                .map((it) => it.options)
                .flat();
              const optionIds = options.map((it) => it.shopOptionId);
              return (
                    <Fragment key={`item ${[shopItemId, ...optionIds].join('-')}`}>
                        <Row onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSelectItem(item);
                        }}
                             fullWidth>
                            <div css={css`pointer-events: none;`} >
                                <Checkbox
                                    isChecked={isSelected(item)} />
                            </div>
                            <Row spacing={12}>
                                <Image src={item.thumbnailImage}
                                       width={60}
                                       height={60} />
                                <Column spacing={4}>
                                    <Text>{item.itemName}</Text>
                                    <Column spacing={2}>
                                        <Text fontWeight='bold'>{item.itemPrice.toLocaleString()}원</Text>
                                        <Text size='s'>
                                            {options.map((it) => it.optionName).join(' | ')}
                                        </Text>
                                    </Column>
                                </Column>
                            </Row>
                        </Row>
                        {(index + 1) < actualApplicableItems.length && (
                            <Divider isThin/>
                        )}
                    </Fragment>
              );
            })}
        </Column>
          <Column fullWidth spacing={10}>
              <Button fullWidth variant='secondary'
                      onClick={closeModal}>
                  취소
              </Button>
          </Column>
      </Column>);
  };

  // 적용 가능한 상품이 없으면 미노출
  if (actualApplicableItemIds.length === 0) {
    return (<Fragment/>);
  }
  return (
        <Row fullWidth className={styles.container}
             onClick={(e) => {
               e.preventDefault();
               e.stopPropagation();
               handleSelectCoupon();
             }}>
            <Checkbox isChecked={isChecked}
                      className={styles.checkbox}
                      onClick={handleSelectCoupon}
            />
            <Column className={styles.card} spacing={8}>
                <Row spacing={4}>
                    <span className={styles.badge}>
                        {dayjs(expiresAt).format('YYYY.MM.DD')}까지
                    </span>
                    {discountValue && (
                        <span className={styles.badge}>
                            {discountMethod === 'FIXED' ? `-${discountValue.toLocaleString()}원`
                              : `-${discountValue}%`}
                    </span>
                    )}
                </Row>
                <Text color={colors.gray7} size='lg'>{title}</Text>
            </Column>
        </Row>
  );
}

export default DiscountCoupon;
